import type { AutoTrackConfig, SdkConfig, SdkConfigInput, TrackingLevel } from './types';

declare global {
	interface Window {
		__emblueLegacyActive?: boolean;
		__emblueEndpoint?: string;
		__emblueWriteKey?: string;
		__emblueHistoryPatched?: boolean;
	}
}

export const SDK_VERSION = (process.env.SDK_VERSION as string) || '2.1.0-poc.0';

/** Replaced at build time by esbuild's `define`. Override per environment
 *  via `TRACKING_DOMAIN=… npm run build`. */
export const TRACKING_DOMAIN = (process.env.TRACKING_DOMAIN as string) || 'pixeltracking-v2.emblue.workers.dev';

export const DEFAULT_LEVEL: TrackingLevel = 'standard';

/**
 * The three tracking levels, as auto-tracking flags. This map is the only place
 * the tiers are defined — the edge worker enforces the same matrix server-side
 * (`src/security/level-filter.ts`), so the two must stay in agreement.
 *
 *   basic     page views, sessions, commerce and explicit API calls, plus
 *             clicks only on elements the site tagged for emBlue
 *   standard  + clicks on every element, outbound, downloads, forms, engagement
 *   advanced  + scroll depth and mouse position (heatmaps)
 *
 * `errors` and `webVitals` are off everywhere: the flags exist but nothing
 * implements them yet, so no level should advertise them.
 */
export const LEVEL_PRESETS: Record<TrackingLevel, Partial<AutoTrackConfig>> = {
	basic: {
		clickScope: 'tagged',
		outbound: false,
		downloads: false,
		forms: false,
		engagement: false,
		scroll: false,
		heatmap: false,
	},
	standard: {
		clickScope: 'all',
		outbound: true,
		downloads: true,
		forms: true,
		engagement: true,
		scroll: false,
		heatmap: false,
	},
	advanced: {
		clickScope: 'all',
		outbound: true,
		downloads: true,
		forms: true,
		engagement: true,
		scroll: true,
		heatmap: true,
	},
};

/** Flags that are the same at every level, before a preset is applied. The
 *  preset always overwrites the ones it names, so these are the level-invariant
 *  defaults only. */
const BASE_AUTO_TRACK: AutoTrackConfig = {
	pageview: true,
	spa: true,
	clicks: true,
	clickScope: 'all',
	outbound: true,
	downloads: true,
	forms: true,
	scroll: false,
	engagement: true,
	errors: false, // opt-in per spec §3.6 — not implemented yet
	webVitals: false, // opt-in per spec §3.7 — not implemented yet
	emailFromForms: true, // on by default — hashes client-side, value never leaves
	dataLayerEmail: false, // opt-in — high false-positive surface
	optin: true, // identity capture is the point of the pixel; no level gates it
	heatmap: false,
};

/** Defaults, i.e. the `standard` level — what a snippet with no level means. */
export const defaultConfig = (): SdkConfig => ({
	endpoint: window.__emblueEndpoint || `https://${TRACKING_DOMAIN}/v2/events`,
	writeKey: window.__emblueWriteKey || null,
	level: DEFAULT_LEVEL,
	sdkVersion: SDK_VERSION,
	debug: false,
	coexistWithLegacy: window.__emblueLegacyActive === true,
	respectDNT: true,
	respectGPC: true,
	consent: {
		required: false,
		defaults: { analytics: true, marketing: false },
	},
	batch: {
		maxEvents: 20,
		maxWaitMs: 2000,
		maxBytes: 32 * 1024,
	},
	autoTrack: { ...BASE_AUTO_TRACK, ...LEVEL_PRESETS[DEFAULT_LEVEL] },
});

/** Unrecognized levels fall back to the default rather than throwing — the SDK
 *  runs on someone else's page and must never break it. */
export function normalizeLevel(level: unknown): TrackingLevel {
	return LEVEL_PRESETS[level as TrackingLevel] ? (level as TrackingLevel) : DEFAULT_LEVEL;
}

/** Maps the pre-level `allClicks` boolean onto `clickScope`, unless the caller
 *  already set `clickScope` explicitly. */
function normalizeLegacy(user: SdkConfigInput): SdkConfigInput {
	const autoTrack = user.autoTrack;
	if (!autoTrack || autoTrack.allClicks === undefined) return user;

	const { allClicks, ...rest } = autoTrack;
	return {
		...user,
		autoTrack: { clickScope: allClicks ? 'all' : 'selective', ...rest },
	};
}

/** Deep-merge user config on top of defaults — handles nested shapes. */
export function mergeConfig(base: SdkConfig, user?: SdkConfigInput): SdkConfig {
	if (!user) return base;
	const normalized = normalizeLegacy(user);
	return {
		...base,
		...normalized,
		consent: { ...base.consent, ...(normalized.consent || {}) },
		batch: { ...base.batch, ...(normalized.batch || {}) },
		autoTrack: { ...base.autoTrack, ...(normalized.autoTrack || {}) },
	};
}

/**
 * Resolves what `init` was given into a full config:
 * defaults → the level's preset → whatever the caller set explicitly.
 *
 * The caller wins over the *snippet's* level, deliberately: a site can hand-tune
 * individual flags. It cannot exceed its level in practice, because the worker
 * filters the batch against the level stored in KV regardless of what the page
 * decides to send.
 *
 * **A remote level wins over an explicit local override, though.** The entry point
 * calls this a second time with the level cached from an ingest response
 * (`core/remote-config.ts`), and since that call layers `LEVEL_PRESETS` on top of
 * the already-merged config, a preset flag overwrites the caller's. So a snippet
 * saying `autoTrack: { heatmap: true }` stops collecting heatmaps once the account
 * is known to be on `basic`. That is the intended outcome — the worker would have
 * dropped those events anyway — but it is the one case where "the caller wins"
 * does not hold.
 */
export function resolveConfig(base: SdkConfig, user?: SdkConfigInput): SdkConfig {
	const level = normalizeLevel(user?.level ?? base.level);
	const withPreset: SdkConfig = {
		...base,
		level,
		autoTrack: { ...base.autoTrack, ...LEVEL_PRESETS[level] },
	};

	// Re-assert the normalized level: mergeConfig spreads `user` wholesale, so a
	// garbage `level` string would otherwise land back on the config.
	return { ...mergeConfig(withPreset, user), level };
}
